import { useToast } from '@airship-platform/airship-patterns';
import { useOrganizationSOWs } from '@airship-platform/airship-proposal';
import {
  FlexBox,
  SanitizedTrimmedHtml,
  SectionCard,
} from '@airship-platform/jam-web/components';
import { SowIcon, TrashIcon } from '@airship-platform/jam-web/icons';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router';
import { useAppointmentProposalOptions } from '../../data-access/appointmentProposalOptions';
import {
  AppointmentProposalOptionSOW,
  useAppointmentProposalOptionSOWs,
} from '../../data-access/appointmentProposalOptionSows';
import { OfflineSyncErrorChip } from '../../patterns';
import AddSowButton from './components/AddSowButton';

const OptionSOW: React.FC = () => {
  const { currentOption } = useAppointmentProposalOptions();
  const { proposalSows, isLoading, deleteAppointmentProposalOptionSOW } =
    useAppointmentProposalOptionSOWs();
  const { sows: organizationSows } = useOrganizationSOWs();
  const navigate = useNavigate();
  const { showToast } = useToast();

  if (!currentOption || !organizationSows.length) {
    return null;
  }

  const isReadOnly = currentOption.isReadOnly;

  const filteredProposalSows = proposalSows.filter(
    (sow) =>
      sow.appointmentProposalOptionId ===
      currentOption.appointmentProposalOptionId
  );

  const hasItems = !!filteredProposalSows.length;

  const handleDelete = (sow: AppointmentProposalOptionSOW) => {
    deleteAppointmentProposalOptionSOW(
      sow.appointmentId,
      sow.appointmentProposalId,
      sow.appointmentProposalOptionId,
      sow.appointmentProposalOptionSOWId
    ).then(() => {
      showToast('Statement of work removed', 'info');
    });
  };

  return (
    <SectionCard
      title="Add statement of work"
      icon={
        <SowIcon width={hasItems ? 70 : 100} height={hasItems ? 70 : 100} />
      }
      hasItems={hasItems}
      to="query-sow"
      action={<AddSowButton variant={hasItems ? 'button' : 'iconButton'} />}
    >
      {filteredProposalSows.map((sow) => (
        <FlexBox
          key={sow.appointmentProposalOptionSOWId}
          alignItems="center"
          justifyContent="space-between"
          gap={3}
          py={2}
        >
          <FlexBox
            flexDirection="column"
            width="100%"
            cursor={'pointer'}
            userSelect={'none'}
            onClick={() =>
              navigate(`sow/${sow.appointmentProposalOptionSOWId}`)
            }
          >
            <FlexBox alignItems="center" gap={1}>
              <Typography variant="h2">{sow.title}</Typography>
              <OfflineSyncErrorChip errors={sow.offlineSyncErrors} />
            </FlexBox>
            <Typography variant="body1">{sow.shortDescription}</Typography>
            <SanitizedTrimmedHtml html={sow.summary} maxLength={10} />
          </FlexBox>
          <IconButton
            color="softGold"
            onClick={() => handleDelete(sow)}
            disabled={isLoading || isReadOnly}
          >
            <TrashIcon />
          </IconButton>
        </FlexBox>
      ))}
    </SectionCard>
  );
};

export default OptionSOW;
